import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useFormik } from "formik";
import { Box } from "@mui/material";
import { sessionService } from "redux-react-session";
import { object, ref, string } from "yup";

import {
  confirmPasswordLabel,
  confirmPasswordValidate,
  enterConfirmPasswordPlaceHolder,
  enterNewPasswordPlaceHolder,
  loadingMsg,
  newPasswordLabel,
  newPasswordSubMsg,
  passwordPolicyMessage,
  passwordSuccess,
  submit,
} from "../messages";
import {
  authOptionsFooterDivider,
  callAPIInterface,
  char8,
  handleAfterLoginProcess,
  lowerCase,
  number,
  passwordPolicy,
  passwordPolicyState,
  sourceTelegramApp,
  upperCase,
} from "../constants";
import { Input } from "../input/Input";
import Text from "../text/Text";
import InputErrorMessage from "../inputErrorMessage/InputErrorMessage";
import Button from "../button/Button";
import CommonAuthHeader from "./CommonAuthHeader";
import BackdropLoader from "../backdropLoader/BackdropLoader";
import LinkStatus from "../linkStatus/LinkStatus";
import { showToast } from "../../redux/actions";

const NewPasswordOptions = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { pathname, search } = useLocation();

  const [activeState, setActiveState] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [linkStatus, setLinkStatus] = useState("");
  const [isLink, setIsLink] = useState(false);

  const handleRedirection = async (user) => {
    const link = searchParams.get("id") || (user && user.verification_link);

    if (link) {
      setLoading(true);
      sessionService
        .saveUser({ verification_link: link })
        .then((_res) => navigate("/new-password"));

      callAPIInterface("GET", `/link/${link}`)
        .then((res) => {
          if (res) {
            setLoading(false);
          }
        })
        .catch((e) => {
          setIsLink(true);
          const err = e?.response?.data?.errors;
          const errorType = err?.[0]?.type?.toLowerCase();
          if (errorType === "link_expired" || errorType === "link_already_used")
            setLinkStatus("expired");
          else navigate("/wallet");
          setLoading(false);
        });
    } else {
      navigate("/forgot-password");
    }
  };

  const handleInitialLoad = async () => {
    const user = await sessionService
      .loadUser()
      .then((res) => res)
      .catch(() => {});
    handleRedirection(user);
  };

  useEffect(() => {
    const searchURL = search.replace("+", "%2B");
    //search URL comes from app
    let locationData = new URLSearchParams(searchURL);

    const source = locationData.get("source");

    locationData.delete("source"); // deleted because when it is redirected from app it will first redirected to web and then telegram mini app from the same page

    //converted to base64 format to create start param value to open telegram mini app.
    const base64URL = btoa(`${pathname}?${locationData.toString()}`);

    if (source === sourceTelegramApp) {
      window.location.href = `https://t.me/${process.env.REACT_APP_TELEGRAM_MINI_APP_USERNAME}?startapp=${base64URL}`;
    } else {
      handleInitialLoad();
    }
  }, []);

  const validationSchema = object({
    new_password: string()
      .matches(char8)
      .matches(upperCase)
      .matches(lowerCase)
      .matches(number)
      .required(""),
    confirm_password: string()
      .oneOf([ref("new_password"), null], confirmPasswordValidate)
      .required(""),
  });

  const initialValues = {
    new_password: "",
    confirm_password: "",
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
  });

  const {
    values,
    touched,
    errors,
    isValid,
    dirty,
    handleChange,
    handleSubmit,
    handleBlur,
  } = formik;

  const defaultErrorMessageProps = {
    touched,
    errors,
  };
  const handleOnSubmit = async (val, resetForm) => {
    setIsSubmitting(true);
    try {
      const user = await sessionService.loadUser();

      const params = {
        verification_link: user.verification_link,
        password: values.new_password,
      };
      await callAPIInterface(
        "POST",
        "/verify-forgot-password",
        JSON.stringify(params)
      );
      dispatch(
        showToast({
          isToastOpen: true,
          toastMessage: passwordSuccess,
          toastVariant: "success",
        })
      );
      sessionService.deleteUser();
      setActiveState([]);

      setTimeout(async () => {
        await handleAfterLoginProcess(navigate);
      }, 1000);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || isSubmitting) {
    return (
      <BackdropLoader
        open={isLoading || isSubmitting}
        alt="loading..."
        text={loadingMsg}
        customClass="loading-in-auth"
      />
    );
  }

  return (
    <CommonAuthHeader shouldShowBackButton={false} showSpeedLogo={false}>
      <>
        {isLink ? (
          !isLoading && <LinkStatus from="resetPassword" type={linkStatus} />
        ) : (
          <>
            <Text className="default-text mt-24" size={22} font="bold">
              {newPasswordLabel}
            </Text>
            <Text className="grey-text mt-8" size={14}>
              {newPasswordSubMsg}
            </Text>
            <Input
              customClass="mt-24"
              fullWidth
              type="password"
              isPasswordVisible={true}
              showLabel
              label={newPasswordLabel}
              placeholder={enterNewPasswordPlaceHolder}
              name="new_password"
              value={values.new_password}
              onChange={(e) => {
                handleChange(e);
                setActiveState(passwordPolicyState(e.target.value));
              }}
              error={touched.new_password && Boolean(errors.new_password)}
            />
            <Box className="mt-8" display="flex" gap={0.5} flexWrap="wrap">
              <Text className="grey-text" size={12} component="span">
                {passwordPolicyMessage}
              </Text>
              <Box
                display="flex"
                gap={0.25}
                component="span"
                width="fit-content"
              >
                {passwordPolicy.map((item, index) => {
                  const lastIndex = passwordPolicy.length - 1;
                  const styles = {
                    color: activeState.includes(item) ? "#0CAD62" : "#FD371E",
                  };
                  return (
                    <Box
                      component="span"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      key={item}
                      gap={0.25}
                    >
                      <Text size={12} style={styles} component="span">
                        {item}
                      </Text>
                      <CheckCircleIcon
                        sx={{ width: "12px", height: "12px", ...styles }}
                        className="info-popper-icon"
                      />
                      {index !== lastIndex && (
                        <Text size={12} className="grey-text" component="span">
                          |
                        </Text>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <Input
              fullWidth
              customClass="mt-24"
              type="password"
              isPasswordVisible={true}
              showLabel
              label={confirmPasswordLabel}
              placeholder={enterConfirmPasswordPlaceHolder}
              name="confirm_password"
              value={values.confirm_password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.confirm_password && Boolean(errors.confirm_password)
              }
            />
            <InputErrorMessage
              {...defaultErrorMessageProps}
              inputName="confirm_password"
            />
            {authOptionsFooterDivider}

            <Button
              fullWidth
              variant="contained"
              label={submit}
              disabled={!(isValid && dirty) || isSubmitting}
              onClick={() => handleOnSubmit()}
            />
          </>
        )}
      </>
    </CommonAuthHeader>
  );
};

export default NewPasswordOptions;
